import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from './lib/config';
import {
  consumePostLoginRedirect,
  pathOfLocation,
  sanitizeRedirectPath
} from './lib/postLoginRedirect';
import { useAuth } from './providers/AuthProvider';
import { AppShell } from './components/layout/AppShell';
import { FullPageSpinner } from './components/layout/FullPageSpinner';
import { LoginPage } from './features/auth/LoginPage';
import { SessionExpiredModal } from './features/auth/SessionExpiredModal';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { FileManagerPage } from './features/files/FileManagerPage';
import { ImapAccountsPage } from './features/imap/ImapAccountsPage';
import { NotFoundPage } from './features/misc/NotFoundPage';
import { CampaignMonitorPage } from './features/scripts/CampaignMonitorPage';
import { RunningScriptsPage } from './features/scripts/RunningScriptsPage';
import { SharedTemplatePage } from './features/share/SharedTemplatePage';
import { useKeepAlive } from './hooks/useKeepAlive';

/**
 * Gate for the authenticated area.
 *
 * `isLoading` is only true for the very first /check-auth. Redirecting during it
 * would bounce every cold load through the login screen before landing on the
 * dashboard, so the spinner is what keeps a refresh from looking like a logout.
 *
 * The redirect carries the current location in router state, so signing in returns
 * the operator to the page they were on — including after a refresh on an expired
 * session, where the expiry dialog never had a chance to show.
 */
function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  useRestoreParkedRedirect(isAuthenticated);

  if (isLoading) return <FullPageSpinner label="Checking your session…" />;
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} replace state={{ from: pathOfLocation(location) }} />;
  }

  return children;
}

/**
 * Finishes a redirect that had to survive a full-page round trip.
 *
 * Google OAuth always returns to `/`, so the login page parks the intended target in
 * sessionStorage before leaving. Consumed on the first authenticated render so it is
 * honoured exactly once.
 */
function useRestoreParkedRedirect(isAuthenticated) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) return;
    const target = consumePostLoginRedirect();
    if (target && target !== pathOfLocation(location)) navigate(target, { replace: true });
  }, [isAuthenticated, location, navigate]);
}

/** Keeps an authenticated operator off the login screen. */
function RedirectIfAuthenticated({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner label="Checking your session…" />;
  if (isAuthenticated) {
    // Same target the login form navigates to, so whichever wins the race after a
    // successful sign-in, the operator lands in the same place.
    const target = sanitizeRedirectPath(location.state?.from) ?? ROUTES.dashboard;
    return <Navigate to={target} replace />;
  }

  return children;
}

export default function App() {
  // Holds the backend connection warm for a tab left open all day. Mounted at the
  // root rather than per page so navigating between pages does not restart the timer.
  useKeepAlive();

  return (
    <>
      <Routes>
        <Route
          path={ROUTES.login}
          element={
            <RedirectIfAuthenticated>
              <LoginPage />
            </RedirectIfAuthenticated>
          }
        />

        {/* Public on purpose: anyone with a share link may view the template. Loading
            it into the composer goes through RequireAuth on the dashboard. */}
        <Route path={ROUTES.share} element={<SharedTemplatePage />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path={ROUTES.dashboard} element={<DashboardPage />} />
          <Route path={ROUTES.fileManager} element={<FileManagerPage />} />
          <Route path={ROUTES.imapAccounts} element={<ImapAccountsPage />} />
          <Route path={ROUTES.scripts} element={<RunningScriptsPage />} />
          <Route path={ROUTES.scriptMonitor} element={<CampaignMonitorPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* One instance for the whole app: every page gets session-expiry handling
          without wiring its own. */}
      <SessionExpiredModal />
    </>
  );
}
